import { ReactNode, useState } from 'react';
import { ArrowDown, Calendar, Download, Folder, ShoppingBag, ToggleLeft, User } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { assetUrl } from '../../Shared/helpers';
import { useLocale } from '../../Shared/locale';
import { Modal } from '../../Shared/Modal/Modal';
import { Campaign, CampaignSortColumn, CampaignType, Event, Pagination, Project } from '../../Shared/types';
import { AButton, Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { Checkbox } from '../../Shared/UI/Checkbox';
import CheckboxLabel from '../../Shared/UI/CheckboxLabel';
import { DateFilterInput } from '../../Shared/UI/DateFilterInput';
import { Icon } from '../../Shared/UI/Icon';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { Pager } from '../../Shared/UI/Pager';
import Picker, { PickerItem } from '../../Shared/UI/Picker';
import { SearchFilterInput } from '../../Shared/UI/SearchFilterInput';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';
import AddCampaignForm from './AddCampaignForm';
import CampaignCard from './CampaignCard';

export interface CampaignsPageProps extends SharedProps {
  campaigns: Pagination<Campaign & {
    project: Project;
    event: Event | null;
    team: Campaign | null;
  }>;
  projects: PickerItem[];
  events: PickerItem[];
  products: PickerItem[];
  exportUrl: string;
}

export default function CampaignsPage({
  campaigns,
  projects,
  events,
  products,
  exportUrl,
  params,
}: CampaignsPageProps) {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  const [showAddCampaignModal, setShowAddCampaignModal] = useState(!!params.add_campaign);
  const [showExportModal, setShowExportModal] = useState(false);

  const showFilters = !!params.search || !!params.project_id || !!params.event_id || !!params.type || !!params.enabled || !!params.product_id || params.created_after || params.created_until || campaigns.meta.total > 0;
  const containerSize = showFilters ? 'lg' : 'md';

  return (
    <>
      <Header>
        <Container size={containerSize}>
          <div className="flex items-center justify-between">
            <HeaderTitle>
              {t('backend:campaigns')}
            </HeaderTitle>
            <div className="space-x-2">
              <Button onClick={() => setShowAddCampaignModal(true)}>
                {t('backend:add_campaign')}
              </Button>
            </div>
          </div>
        </Container>
      </Header>

      <Container size={containerSize} className="space-y-6">
        {showFilters && (
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-3 space-y-6">
              <div className="col-span-3 space-y-6">
                <div className="space-y-4">
                  <InputGroup>
                    <InputLabel className="font-medium text-sm text-slate-500" htmlFor="search">
                      {t('shared:filter.search')}
                    </InputLabel>
                    <SearchFilterInput placeholder="" id="search" />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="sort">
                      {t('backend:order')}
                    </InputLabel>
                    <Picker
                      name="sort"
                      items={[
                        {
                          id: '',
                          title: t('backend:most_recent'),
                        },
                        {
                          id: CampaignSortColumn.FundsRaised,
                          title: t('backend:most_funds_raised'),
                        },
                      ]}
                      selected={typeof params.sort === 'string' ? params.sort : ''}
                      icon={<ArrowDown />}
                      payload={() => ({ page: undefined })}
                      id="sort"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="font-medium text-sm text-slate-500" htmlFor="project">
                      {t('backend:project')}
                    </InputLabel>
                    <Picker
                      name="project_id"
                      items={projects}
                      selected={typeof params.project_id === 'string' ? params.project_id : null}
                      icon={<Folder />}
                      optional
                      id="project"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="font-medium text-sm text-slate-500" htmlFor="event">
                      {t('backend:event')}
                    </InputLabel>
                    <Picker
                      name="event_id"
                      items={events}
                      selected={typeof params.event_id === 'string' ? params.event_id : null}
                      icon={<Calendar />}
                      optional
                      id="event"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="campaign_type">
                      {t('backend:campaign_type')}
                    </InputLabel>
                    <Picker
                      name="type"
                      items={[
                        {
                          id: CampaignType.Individual,
                          title: t('backend:campaign_type_individual'),
                        },
                        {
                          id: CampaignType.Team,
                          title: t('backend:campaign_type_team'),
                        },
                      ]}
                      selected={typeof params.type === 'string' ? params.type : null}
                      icon={<User />}
                      optional
                      payload={() => ({ page: undefined })}
                      id="campaign_type"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="status">
                      {t('backend:status')}
                    </InputLabel>
                    <Picker
                      name="enabled"
                      items={[
                        {
                          id: '1',
                          title: t('backend:live'),
                        },
                        {
                          id: '0',
                          title: t('backend:offline'),
                        },
                      ]}
                      selected={typeof params.enabled === 'string' ? params.enabled : null}
                      icon={<ToggleLeft />}
                      optional
                      payload={() => ({ page: undefined })}
                      id="status"
                    />
                  </InputGroup>
                  {products.length > 0 && (
                    <InputGroup>
                      <InputLabel className="font-medium text-sm text-slate-500" htmlFor="product">
                        {t('backend:product')}
                      </InputLabel>
                      <Picker
                        name="product_id"
                        items={products}
                        selected={typeof params.product_id === 'string' ? params.product_id : null}
                        icon={<ShoppingBag />}
                        optional
                        id="product"
                      />
                    </InputGroup>
                  )}
                  <InputGroup>
                    <InputLabel className="font-medium text-sm text-slate-500" htmlFor="created_after">
                      {t('backend:from_date')}
                    </InputLabel>
                    <DateFilterInput
                      name="created_after"
                      selectsStart
                      endDate={typeof params.created_until === 'string' ? params.created_until : undefined}
                      id="created_after"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="font-medium text-sm text-slate-500" htmlFor="created_until">
                      {t('backend:until_date')}
                    </InputLabel>
                    <DateFilterInput
                      name="created_until"
                      selectsEnd
                      startDate={typeof params.created_after === 'string' ? params.created_after : undefined}
                      id="created_until"
                    />
                  </InputGroup>
                </div>
                <div>
                  <Button
                    onClick={() => setShowExportModal(true)}
                    variant="secondary"
                    size="sm"
                    disabled={campaigns.meta.total === 0 || campaigns.meta.total > 3000}
                  >
                    <Icon className="mr-1">
                      <Download />
                    </Icon>
                    {t('backend:export_selection')}
                    {' '}
                    ({formatNumber(campaigns.meta.total)})
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-span-9 space-y-6">
              {campaigns.data.length > 0 && (
                <div className="space-y-2">
                  {campaigns.data.map((campaign) => (
                    <CampaignCard
                      campaign={campaign}
                      key={campaign.id}
                    />
                  ))}
                </div>
              )}

              {campaigns.meta.last_page > 1 && (
                <Pager data={campaigns} />
              )}

              {showFilters && campaigns.meta.total === 0 && (
                <Card className="text-center text-slate-500 py-24">
                  <img
                    src={assetUrl('images/no-results.svg')}
                    alt={t('backend:no_results')}
                    width={120}
                    className="block mx-auto mb-6"
                  />
                  {t('backend:no_results')}
                </Card>
              )}
            </div>
          </div>
        )}

        {!showFilters && (
          <Card className="text-center text-slate-500">
            <h2>
              {t('backend:no_campaigns_yet')}
            </h2>
            {t('backend:no_campaigns_yet_description')}
          </Card>
        )}
      </Container>

      <Modal isOpen={showAddCampaignModal} onClose={() => setShowAddCampaignModal(false)} size="sm">
        <AddCampaignForm projects={projects} />
      </Modal>

      <Modal isOpen={showExportModal} onClose={() => setShowExportModal(false)} size="sm">
        <ExportCampaignsModal count={campaigns.meta.total} exportUrl={exportUrl} onExport={() => setShowExportModal(false)} />
      </Modal>
    </>
  );
}

CampaignsPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;

const ExportCampaignsModal = ({
  count,
  exportUrl,
  onExport,
}: {
  count: number;
  exportUrl: string;
  onExport: () => void;
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  const [splitLastNames, setSplitLastNames] = useState(false);

  const getExportUrl = () => {
    const url = new URL(exportUrl);

    if (splitLastNames) {
      url.searchParams.append('split_last_names', '1');
    }

    return url.toString();
  };

  return (
    <div className="space-y-6">
      <h3 className="py-1">
        {t('backend:export_campaigns', { total: formatNumber(count) })}
      </h3>

      <hr />

      <InputGroup>
        <CheckboxLabel htmlFor="export_split_last_names">
          <Checkbox
            onChange={() => setSplitLastNames((split) => !split)}
            checked={splitLastNames}
            className="mt-1"
            id="export_split_last_names"
          />
          <span>
            {t('backend:split_last_names_label')}
          </span>
        </CheckboxLabel>
      </InputGroup>

      <hr />

      <div className="text-right">
        <AButton
          href={getExportUrl()}
          onClick={() => onExport()}
        >
          {t('backend:export')}
        </AButton>
      </div>
    </div>
  );
};
